import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { patientPortal } from '@/api/patientPortal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { PATIENT_PORTAL_IMAGES } from './patientPortalImagery';
import { usePatientPortalBranding } from './PatientPortalBrandingProvider';
import { PATIENT_PORTAL_LOGIN_LOGO_PATH, PATIENT_PORTAL_TAGLINE } from './patientPortalBranding';
import { PatientEmailRegisterDialog } from './PatientEmailRegisterDialog';

type AuthView = 'password' | 'setPassword' | 'otp';

type PatientPick = {
  id: string;
  name: string;
  uhid: string | null;
  email?: string | null;
  hasPassword?: boolean;
};

const NOT_REGISTERED_MSG =
  'This number is not registered with the hospital. Please visit the hospital and get yourself registered.';

function isEmailIdentifier(value: string): boolean {
  return value.trim().includes('@');
}

function authViewForPatient(p: PatientPick): AuthView {
  return p.hasPassword ? 'password' : 'setPassword';
}

export default function PatientPortalLogin() {
  const navigate = useNavigate();
  const branding = usePatientPortalBranding();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'identifier' | 'auth'>('identifier');
  const [authView, setAuthView] = useState<AuthView>('password');
  const [patients, setPatients] = useState<PatientPick[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [showEmailRegister, setShowEmailRegister] = useState(false);

  if (localStorage.getItem('patient_token') && !showEmailRegister && !pendingToken) {
    return <Navigate to="/patient-loyalty" replace />;
  }

  const selectedPatient = patients.find((p) => p.id === selectedPatientId) ?? null;
  const authHospitalId = branding.hospitalId ?? undefined;

  const finishLogin = (token: string) => {
    localStorage.setItem('patient_token', token);
    setPendingToken(null);
    setShowEmailRegister(false);
    navigate('/patient-loyalty');
  };

  const openEmailPrompt = (token: string) => {
    localStorage.setItem('patient_token', token);
    setPendingToken(token);
    setShowEmailRegister(true);
  };

  const completeAuthResult = (res: {
    token?: string;
    needsSetPassword?: boolean;
    setupToken?: string;
    promptAddEmail?: boolean;
    patient?: { email?: string | null };
  }) => {
    if (res.needsSetPassword && res.setupToken) {
      setSetupToken(res.setupToken);
      setAuthView('setPassword');
      return;
    }
    if (!res.token) {
      toast.error('Sign in could not be completed');
      return;
    }
    const needsEmail =
      res.promptAddEmail === true ||
      !(res.patient?.email?.trim() || selectedPatient?.email?.trim());
    if (needsEmail) {
      openEmailPrompt(res.token);
    } else {
      finishLogin(res.token);
    }
  };

  const pickPatient = (id: string) => {
    setSelectedPatientId(id);
    const p = patients.find((x) => x.id === id);
    if (p && authView !== 'otp') {
      setAuthView(authViewForPatient(p));
    }
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setOtp('');
    setSetupToken(null);
    setDevOtp(null);
  };

  const lookupAccounts = async () => {
    setLoading(true);
    setDevOtp(null);
    setSetupToken(null);
    try {
      const res = await patientPortal.lookupAccounts(identifier.trim(), authHospitalId);
      const list = (res.patients ?? []) as PatientPick[];
      if (list.length === 0) {
        if (isEmailIdentifier(identifier)) {
          toast.error(
            'This email is not registered with the hospital. Please visit the hospital and get yourself registered.'
          );
        } else {
          toast.error(NOT_REGISTERED_MSG);
        }
        return;
      }
      setPatients(list);
      if (list.length === 1) {
        setSelectedPatientId(list[0].id);
        setAuthView(authViewForPatient(list[0]));
      } else {
        setSelectedPatientId(null);
        setAuthView('password');
      }
      setStep('auth');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not find account');
    } finally {
      setLoading(false);
    }
  };

  const loginPassword = async () => {
    if (!selectedPatientId) {
      toast.error('Select your profile');
      return;
    }
    setLoading(true);
    try {
      const res = await patientPortal.loginWithPassword(selectedPatientId, password);
      completeAuthResult(res);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const registerPassword = async () => {
    if (!selectedPatientId) {
      toast.error('Select your profile');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = setupToken
        ? await patientPortal.setPasswordInitial({ setupToken, newPassword })
        : await patientPortal.setPasswordInitial({
            patientId: selectedPatientId,
            identifier: identifier.trim(),
            newPassword,
            hospitalId: authHospitalId,
          });
      completeAuthResult(res);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not set password');
    } finally {
      setLoading(false);
    }
  };

  const switchToOtp = async () => {
    if (!selectedPatientId) {
      toast.error('Select your profile');
      return;
    }
    setAuthView('otp');
    setOtp('');
    setDevOtp(null);
    await sendOtp();
  };

  const sendOtp = async () => {
    if (!selectedPatientId) {
      toast.error('Select your profile');
      return;
    }
    setLoading(true);
    setDevOtp(null);
    try {
      const res = await patientPortal.sendOtp({
        patientId: selectedPatientId,
        delivery: 'WHATSAPP',
        identifier: identifier.trim(),
        hospitalId: authHospitalId,
      });
      if (res.devOtp) setDevOtp(res.devOtp);
      if (res.phoneMismatchWarning) {
        toast.warning(res.phoneMismatchWarning, { duration: 14000 });
      }
      const ch = res.deliveryChannel as string | undefined;
      const dest = res.destinationMasked ? ` to ${res.destinationMasked}` : '';
      const hint =
        (res.deliveryHint as string | undefined) ||
        'Check WhatsApp from T.R.U.E. Hospitals (+91 70117 43241), including the Updates tab.';
      if (ch === 'whatsapp_text_fallback') {
        toast.success(`OTP sent${dest} (WhatsApp chat message)`, { description: hint, duration: 14000 });
      } else if (ch?.startsWith('whatsapp_template')) {
        toast.success(`Sign-in code sent${dest} on WhatsApp`, { description: hint, duration: 14000 });
      } else {
        toast.success(res.destinationMasked ? `OTP sent${dest}` : 'OTP sent', {
          description: hint,
          duration: 14000,
        });
      }
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not send OTP on WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!selectedPatientId) {
      toast.error('Select your profile');
      return;
    }
    setLoading(true);
    try {
      const res = await patientPortal.verifyOtp({
        patientId: selectedPatientId,
        otp,
        identifier: identifier.trim(),
      });
      completeAuthResult(res);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setStep('identifier');
    setAuthView('password');
    setPatients([]);
    setSelectedPatientId(null);
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setOtp('');
    setSetupToken(null);
    setDevOtp(null);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block min-h-[320px]">
        <img
          src={PATIENT_PORTAL_IMAGES.home}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-teal-950/85 via-teal-900/70 to-emerald-900/50" />
        <div className="relative flex h-full flex-col justify-end p-10 text-white">
          <div>
            <p className="text-sm font-medium text-teal-50/95">{PATIENT_PORTAL_TAGLINE}</p>
            <p className="mt-3 max-w-md text-teal-50/90 text-sm leading-relaxed">
              Sign in with your registered mobile or email. Family members who share a phone number
              each have their own profile — choose yours after entering the number.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center bg-gradient-to-b from-slate-50 to-white p-6 sm:p-10">
        <img
          src={branding.logoUrl || PATIENT_PORTAL_LOGIN_LOGO_PATH}
          alt=""
          className="h-56 sm:h-64 w-auto max-w-[min(100%,640px)] object-contain mb-8"
          decoding="async"
        />
        <div className="w-full max-w-md rounded-2xl bg-white/95 backdrop-blur shadow-xl ring-1 ring-slate-200/80 p-8 space-y-5">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {step === 'auth' && authView === 'setPassword' ? 'Create your password' : 'Welcome back'}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {step === 'identifier'
                ? 'Enter your registered mobile or email'
                : authView === 'setPassword'
                  ? 'Choose a password for future sign-ins'
                  : authView === 'otp'
                    ? 'Sign in with a one-time WhatsApp code'
                    : 'Sign in with your password'}
            </p>
          </div>

          {step === 'identifier' ? (
            <>
              <div>
                <Label>Email or mobile number</Label>
                <Input
                  className="mt-1 h-11"
                  placeholder="you@email.com or 9876543210"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  autoComplete="username"
                />
              </div>
              <Button
                className="w-full h-11 bg-teal-800 hover:bg-teal-900"
                onClick={lookupAccounts}
                disabled={loading || !identifier.trim()}
              >
                Continue
              </Button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="text-sm text-teal-700 underline"
                onClick={resetFlow}
              >
                ← Change email / mobile
              </button>

              {patients.length > 1 && (
                <div className="space-y-2">
                  <Label>Who is signing in?</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {patients.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => pickPatient(p.id)}
                        className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition ${
                          selectedPatientId === p.id
                            ? 'border-teal-600 bg-teal-50 ring-1 ring-teal-600'
                            : 'border-slate-200 hover:border-teal-300'
                        }`}
                      >
                        <span className="font-medium text-slate-900">{p.name}</span>
                        {p.uhid ? (
                          <span className="block text-xs text-muted-foreground mt-0.5">
                            UHID {p.uhid}
                          </span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedPatient && patients.length === 1 && (
                <p className="text-sm text-slate-600">
                  {authView === 'setPassword' ? 'Setting up for' : 'Signing in as'}{' '}
                  <strong>{selectedPatient.name}</strong>
                  {selectedPatient.uhid ? ` · ${selectedPatient.uhid}` : ''}
                </p>
              )}

              {authView === 'setPassword' ? (
                <>
                  <div>
                    <Label>New password</Label>
                    <Input
                      type="password"
                      className="mt-1 h-11"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <Label>Confirm password</Label>
                    <Input
                      type="password"
                      className="mt-1 h-11"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                  <Button
                    className="w-full h-11 bg-teal-800 hover:bg-teal-900"
                    onClick={registerPassword}
                    disabled={loading || !selectedPatientId}
                  >
                    Save & continue
                  </Button>
                  {!setupToken && selectedPatient?.hasPassword ? (
                    <p className="text-center text-sm">
                      <button
                        type="button"
                        className="text-teal-700 underline"
                        onClick={() => setAuthView('password')}
                      >
                        Already have a password? Sign in
                      </button>
                    </p>
                  ) : null}
                </>
              ) : authView === 'password' ? (
                <>
                  <div>
                    <Label>Password</Label>
                    <Input
                      type="password"
                      className="mt-1 h-11"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </div>
                  <Button
                    className="w-full h-11 bg-teal-800 hover:bg-teal-900"
                    onClick={loginPassword}
                    disabled={loading || !selectedPatientId || !password}
                  >
                    Sign in
                  </Button>
                  <p className="text-center text-sm">
                    <Link
                      to="/patient-loyalty/forgot-password"
                      className="text-teal-700 underline"
                    >
                      Forgot password?
                    </Link>
                  </p>
                  <div className="relative py-1">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-2 text-muted-foreground">or</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11"
                    onClick={() => void switchToOtp()}
                    disabled={loading || !selectedPatientId}
                  >
                    Sign in with WhatsApp OTP
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    OTP is sent on WhatsApp to the mobile number saved on your hospital record.
                  </p>
                  <Button
                    variant="outline"
                    className="w-full h-11"
                    onClick={sendOtp}
                    disabled={loading || !selectedPatientId}
                  >
                    Resend OTP on WhatsApp
                  </Button>
                  <div>
                    <Label>One-time code</Label>
                    <Input
                      className="mt-1 h-11"
                      placeholder="6-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      maxLength={6}
                    />
                    {devOtp && (
                      <p className="text-amber-700 text-sm mt-1">Dev OTP: {devOtp}</p>
                    )}
                  </div>
                  <Button
                    className="w-full h-11 bg-teal-800 hover:bg-teal-900"
                    onClick={verifyOtp}
                    disabled={loading || !otp || !selectedPatientId}
                  >
                    Verify & sign in
                  </Button>
                  <p className="text-center text-sm">
                    <button
                      type="button"
                      className="text-teal-700 underline"
                      onClick={() => {
                        setAuthView(selectedPatient ? authViewForPatient(selectedPatient) : 'password');
                        setOtp('');
                        setDevOtp(null);
                      }}
                    >
                      ← Back to password sign-in
                    </button>
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <PatientEmailRegisterDialog
        open={showEmailRegister}
        onOpenChange={setShowEmailRegister}
        patientName={selectedPatient?.name}
        onDone={() => {
          const token = pendingToken || localStorage.getItem('patient_token');
          if (token) finishLogin(token);
        }}
      />
    </div>
  );
}
